function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { ValueCache } from '@leverege/value-cache';
import { Pane, PropertyGrid, Content, Button, Text, Popup } from '@leverege/ui-elements';
import { Config, Plugins, Match } from '@leverege/plugin';
import { TitleBar } from '@leverege/ui-plugin';
import { Attributes } from '@leverege/ui-attributes';
import { camelCaseToTitleCase } from '@leverege/string-util';
import I18N from '@leverege/i18n';
import OpenApiItemEditorUtil from './OpenApiItemEditorUtil.js';
import S from './OpenApiItemEditor.css';
import { OpenApiAttributeEditorFactory } from './attributeEditors/Factory.js';
import { OpenApiAttributeFormValidatorFactory } from './attributeValidators/Factory.js';
const {
  t
} = I18N.ns('openApi.ItemEditor');
export default class OpenApiItemEditor extends React.Component {
  static type = 'default';
  static propTypes = {
    mode: PropTypes.string,
    attributeOptionKey: PropTypes.string,
    getAttributes: PropTypes.func,
    cancelText: PropTypes.string,
    submitText: PropTypes.string,
    onCancel: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    submitFormat: PropTypes.string
  };
  static defaultProps = {
    mode: 'create',
    attributeOptionKey: 'createOptions',
    submitFormat: 'put',
    getAttributes: OpenApiItemEditorUtil.defaultGetAttributes,
    cancelText: 'Cancel',
    submitText: 'Submit'
  };
  constructor(props) {
    super(props);
    const {
      mode
    } = props;
    this.state = {
      breakpoint: this.getBreakPoint(window?.innerWidth),
      errors: {},
      showErrors: false
    };
    this.cache = new ValueCache();
    if (mode === 'create') {
      this.state.changes = this.getDefaultValues();
    }
  }
  getBreakPoint(size) {
    return size > Config.get('ResponsiveUtility', 'smallBreakpoint', 1024) ? 'big' : 'small';
  }
  onResize = evt => {
    this.setState(state => {
      const newState = this.getBreakPoint(evt.target?.innerWidth);
      if (newState !== state.breakpoint) {
        return {
          breakpoint: newState
        };
      }
      return null;
    });
  };
  componentDidMount() {
    window.addEventListener('resize', this.onResize);
    this.validateInitialData();
  }
  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
  }
  validateInitialData() {
    const data = this.getData();
    const attributes = this.getAttributes();
    let errors = {};
    const validationContext = this.getValidationContext();
    attributes.forEach(attr => {
      const validator = this.getValidator(attr);
      const attrErrors = [];
      if (validator) {
        try {
          const value = attr.get(data);
          const result = validator.validate(value, validationContext);
          if (result !== true) {
            if (Array.isArray(result)) {
              attrErrors.push(...result);
            } else {
              attrErrors.push(result);
            }
          }
        } catch (err) {
          attrErrors.push(err);
        }
      }
      if (attrErrors.length > 0) {
        errors = {
          ...errors,
          [attr.name]: attrErrors
        };
      }
    });
    this.setState({
      errors
    });
  }
  hasErrors() {
    const {
      errors
    } = this.state;
    return Object.values(errors).some(val => val?.length > 0);
  }
  onSubmit = evt => {
    const {
      onSubmit,
      eventData,
      submitFormat
    } = this.props;
    if (this.hasErrors()) {
      this.setState({
        showErrors: true
      });
      return;
    }
    let value;
    if (submitFormat === 'put') {
      value = this.getData();
    } else if (submitFormat === 'patch') {
      value = this.getPatch();
    }
    onSubmit?.({
      value,
      data: eventData,
      original: evt
    });
  };
  onCancel = evt => {
    const {
      onCancel,
      eventData
    } = this.props;
    onCancel?.({
      data: eventData,
      original: evt
    });
  };
  getFilters() {
    const {
      objectType,
      relationship,
      mode
    } = this.props;
    const plgs = Plugins.get('OpenApiItemEditorAttributeFilter').filter(plg => Match.isMatch({
      objectType,
      path: relationship.path,
      mode
    }, plg.matches));
    return plgs.map(plg => plg.filter);
  }
  filterAttrs = attr => {
    const {
      mode
    } = this.props;
    let editable = false;
    if (mode === 'create') {
      editable = attr?.source?.creatable;
    }
    if (mode === 'edit') {
      editable = attr?.source?.editable;
    }
    const plgFilters = this.getFilters();
    return attr?.set && editable && !attr?.forwarded && plgFilters.every(fn => fn(attr));
  };
  getAttributes() {
    const {
      getAttributes,
      target,
      attributeOptionKey,
      relationship
    } = this.props;
    return this.cache.calc((target, apiName, getAttributes) => {
      const attrs = getAttributes(target, apiName, this.filterAttrs) || [];
      // sort them
      attrs.sort(OpenApiItemEditorUtil.getSort(attributeOptionKey));
      return attrs;
    }, target, relationship.apiName, getAttributes);
  }
  getValidator(attr) {
    const {
      attributeOptionKey
    } = this.props;
    const validatorConf = attr?.source?.[attributeOptionKey]?.validator || attr.name;
    const validator = OpenApiAttributeFormValidatorFactory.create(validatorConf, attr);
    return validator;
  }
  getValidationContext() {
    const {
      target
    } = this.props;
    const {
      changes
    } = this.state;
    return {
      data: this.getData(),
      target,
      changes
    };
  }
  onFieldChange = ({
    value,
    data
  }) => {
    const {
      attribute
    } = data;
    const validator = this.getValidator(attribute);
    const errors = [];
    if (validator) {
      try {
        const result = validator.validate(value, this.getValidationContext());
        if (result !== true) {
          if (Array.isArray(result)) {
            errors.push(...result);
          } else {
            errors.push(result);
          }
        }
      } catch (err) {
        errors.push(err);
      }
    }
    if (errors.length > 0) {
      this.setState(state => ({
        errors: {
          ...state.errors,
          [attribute.name]: errors
        }
      }));
    } else {
      this.setState(state => ({
        changes: {
          ...state.changes,
          [attribute.name]: value
        },
        errors: {
          ...state.errors,
          [attribute.name]: null
        }
      }));
    }
  };
  getContext() {
    return this.props;
  }
  getDefaultValues() {
    const {
      target
    } = this.props;
    const attrs = this.getAttributes();
    const changes = {};
    attrs.forEach(attr => {
      if (attr.get(target?.data) === undefined) {
        const defaultValue = attr?.getDefaultValue?.(target);
        if (defaultValue !== undefined) {
          changes[attr.name] = defaultValue;
        }
      }
    });
    return changes;
  }
  getData() {
    const {
      target
    } = this.props;
    const {
      changes
    } = this.state;
    let toReturn = target?.data ?? {};
    if (changes) {
      Object.keys(changes).forEach(attrName => {
        const attr = Attributes.getAttribute(attrName, target?.type);
        toReturn = attr.set(toReturn, changes[attrName], this.getContext());
      });
    }
    return toReturn;
  }
  getPatch() {
    const {
      target,
      data
    } = this.props;
    const {
      changes
    } = this.state;
    const toReturn = [];
    if (changes) {
      Object.keys(changes).forEach(attrName => {
        const attr = Attributes.getAttribute(attrName, target?.type);
        const change = attr.getPatch?.(data, changes[attrName], this.getContext());
        if (Array.isArray(change)) {
          toReturn.push(...change);
        } else if (change != null) {
          toReturn.push(change);
        }
      });
    }
    return toReturn;
  }
  getAttributeEditorLabel(attr) {
    const {
      attributeOptionKey
    } = this.props;
    return /*#__PURE__*/React.createElement(React.Fragment, null, camelCaseToTitleCase(attr?.source?.[attributeOptionKey]?.label || attr?.displayName || attr?.name), attr?.source?.[attributeOptionKey]?.hint ? /*#__PURE__*/React.createElement(Popup, {
      title: "Help",
      icon: "fa fa-question-circle"
    }, /*#__PURE__*/React.createElement(Pane, {
      className: S.hint,
      variant: "dataCard"
    }, /*#__PURE__*/React.createElement(Text, null, attr?.source?.[attributeOptionKey]?.hint))) : null);
  }
  getEditor = attr => {
    const {
      attributeOptionKey,
      model
    } = this.props;
    const {
      errors,
      showErrors
    } = this.state;
    const editorOptions = model?.editors?.[attr.name];
    const editorType = editorOptions?.editor ?? attr?.source?.[attributeOptionKey]?.editor ?? attr?.valueType;
    const editorProps = attr?.source?.[attributeOptionKey]?.editorProps;
    const containerProps = attr?.source?.[attributeOptionKey]?.containerProps;
    // validator factory goes here
    const innerEditor = OpenApiAttributeEditorFactory.create(editorType, {
      attr,
      onChange: this.onFieldChange,
      data: this.getData(),
      context: this.getContext(),
      // validator : this.getValidator( attr ),
      ...editorProps,
      ...model?.editors?.[attr.name]?.props
    });
    if (!innerEditor || editorOptions?.enabled === false) {
      return null;
    }
    return /*#__PURE__*/React.createElement(PropertyGrid.Item, _extends({
      key: attr.name,
      label: this.getAttributeEditorLabel(attr),
      fill: true
    }, containerProps), /*#__PURE__*/React.createElement(Pane, {
      layout: "flex:colM"
    }, innerEditor, /*#__PURE__*/React.createElement(Text, {
      className: classnames(S.formError, showErrors ? S.visible : S.hidden),
      variant: "formError|inputCaptionSmallError"
    }, errors[attr.name]?.join('; ') ?? '')));
  };
  getErrorAttributeNames() {
    const {
      errors
    } = this.state;
    const errAttrNames = Object.entries(errors).filter(([key, value]) => value?.length > 0).map(([key]) => key);
    const allAttrs = this.getAttributes();
    const errAttrs = errAttrNames.map(errAttrname => allAttrs.find(attr => attr.name === errAttrname));
    return errAttrs.map(attr => attr.displayName).join(', ');
  }
  render() {
    const {
      variant,
      titleVariant = 'dialogTitle',
      title,
      titleIcon,
      submitText,
      cancelText,
      headerVariant,
      bodyVariant,
      footerVariant
    } = this.props;
    const {
      breakpoint,
      showErrors
    } = this.state;
    const vnt = variant || (breakpoint === 'small' ? 'mobileItemForm' : 'itemForm');
    const attributes = this.getAttributes().filter(a => !a?.source?.model?.['x-lev-read-only']);
    const editors = attributes.map(this.getEditor).filter(e => e);
    const headerVnt = headerVariant || (breakpoint === 'small' ? 'mobileItemFormHeader' : 'itemFormHeader');
    const bodyVnt = bodyVariant || (breakpoint === 'small' ? 'mobileItemFormBody' : 'itemFormBody');
    const footerVnt = footerVariant || (breakpoint === 'small' ? 'mobileItemFormFooter' : 'itemFormFooter');
    return /*#__PURE__*/React.createElement(Pane, {
      variant: vnt
    }, /*#__PURE__*/React.createElement(Content, {
      relative: breakpoint === 'big'
    }, /*#__PURE__*/React.createElement(Content.Header, {
      variant: `${headerVnt}|formHeader`
    }, /*#__PURE__*/React.createElement(TitleBar, {
      variant: titleVariant,
      title: title,
      icon: titleIcon
    })), /*#__PURE__*/React.createElement(Content.Area, {
      variant: `${bodyVnt}|formBody`,
      className: breakpoint === 'small' ? S.formMobile : S.form
    }, /*#__PURE__*/React.createElement(PropertyGrid, {
      inline: false,
      vertical: true,
      className: S.fullWidth
    }, editors)), /*#__PURE__*/React.createElement(Content.Footer, {
      variant: `${footerVnt}|formButtons`,
      layout: "flex:rowSpaceBetween"
    }, showErrors && this.hasErrors() ? /*#__PURE__*/React.createElement(Text, {
      className: S.formErrorList,
      variant: "formError|inputCaptionSmallError"
    }, t('formErrors', {
      attrs: this.getErrorAttributeNames()
    })) : /*#__PURE__*/React.createElement("div", null), /*#__PURE__*/React.createElement(Pane, {
      layout: "flex:rowM"
    }, /*#__PURE__*/React.createElement(Button, {
      variant: "secondary",
      type: "button",
      onClick: this.onCancel
    }, cancelText), /*#__PURE__*/React.createElement(Button, {
      variant: "primary",
      onClick: this.onSubmit
    }, submitText)))));
  }
}